import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core';

import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ROUTE_PATHS } from '../../contants/ROUTE_PATHS';

import { newUser } from '../../store/index';

import { baseTheme } from '../../theme';

import CreatePostButton from '../../components/post-components/action-buttons/create-post-button';
import PrintPageButton from '../../components/post-components/action-buttons/print-page-button';
import FaqActionButton from '../../components/post-components/action-buttons/tips-faq-button';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    mainHeading: {
      paddingBottom: theme.spacing(4),
    },
    contentSpacer: {
      paddingTop: theme.spacing(1),
    },
    contentItem: {
      paddingBottom: theme.spacing(4),
    },
    contentHeading: {
      paddingBottom: theme.spacing(1),
    },
    quoteBlock: {
      textAlign: 'center',
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    iconSpacer: {
      position: 'relative',
      top: '5px',
    },
  }),
);

export default function AdminWelcomePage() {
  const classes = useStyles(baseTheme);
  const history = useHistory();
  const dispatch = useDispatch();

  const groupLink = (event: any) => {
    event.preventDefault();
    dispatch(newUser(false));
    history.push(ROUTE_PATHS.ADMIN_GROUPS);
  };

  return (
    <Grid item container direction="column" className={classes.root}>
      <Grid item className={classes.mainHeading}>
        <Typography variant="h4" color="primary">
          Welcome to Reliht!
        </Typography>
      </Grid>

      <Grid item className={classes.contentItem}>
        <Typography variant="h6" color="primary" className={classes.contentHeading}>
          Please read or print instructions below <u>BEFORE</u> creating your first post.
        </Typography>
        <Typography variant="body1">
          To print the tips below, click <PrintPageButton iconOnly
            className={classes.iconSpacer} />.
          If you want to view these tips later, simply
          click on <FaqActionButton iconOnly
            className={classes.iconSpacer} /> at the top of your post pages.
        </Typography>
        <Typography variant="h6" color="primary" className={classes.contentHeading}>
          Click <CreatePostButton iconOnly
            className={classes.iconSpacer} /> to Create Your First Post
        </Typography>
      </Grid>

      <Grid item className={classes.contentItem}>
        <Typography variant="h6" color="primary" className={classes.contentHeading}>
          The Importance of Assigning Groups and 3 Ways To Post
        </Typography>
        <Typography variant="body1">
          <b>
            For each post you create, you will have the option to assign a group.
          </b>
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          <b>
            You may: 1) assign NO group and so keep your post a private journal entry,
            2) assign your post to a closed group you have created that only your
            approved followers can join, or 3) assign your post to a public group
            that everyone can follow.
          </b>
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          The first and most important thing to remember, your post remains private
          until you assign a group to the post. Also, the closed or public post does
          not go live until you toggle the switch to LIVE.
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          You will have fields to enter a title, date, note and highlights.
          Highlights are images and/or short sentences or bullet points
          that show in your app feed above your post.
          It's a great way to catch someone's attention to your post.
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          When you create a post, we will be autosaving for you.  You’ll notice that
          each post appears in your post list. Don’t be alarmed, as mentioned
          above—<b>posts cannot be seen by anyone until you assign a group <u>AND</u> go
            “LIVE”—TWO STEPS needed to go LIVE—so you can have extra confidence in when
            posts can be seen by others.</b>
        </Typography>
      </Grid>

      <Grid item className={classes.contentItem}>
        <Typography variant="h6" color="primary" className={classes.contentHeading}>
          1 - Private Post
        </Typography>
        <Typography variant="body1">
          To keep any post private, so only you can see it, simply keep the group
          field empty.  These private posts are your personal journal and a great
          way to get started.
        </Typography>
      </Grid>

      <Grid item className={classes.contentItem}>
        <Typography variant="h6" color="primary" className={classes.contentHeading}>
          2 - Closed Group Post
        </Typography>
        <Typography variant="body1">
          Closed groups require followers “JOIN”.  Once an individual has clicked to
          join your closed group, an “Approve Request” link will appear on your
          Groups page.  Requests from followers to join a closed group must be approved
          by you.  Once you approve that request to join, the follower will be able to
          see your live posts to that closed group.  You will also have the option to
          remove closed group followers from your Groups page.
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          Begin by assigning the post to a closed group name.  Your group name should
          reflect a group, event, study, series, topic, campaign, etc. and help identify
          your group to followers.  You will most likely have multiple closed groups, so
          the group name should be unique and descriptive.  An example might be “Wednesday
          Night Small Group”.  And along with group names in your posts, to help identify
          you, followers will see your leader, church or ministry name as well as a title
          and date posted.
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          To go LIVE with your closed group post, you will need to toggle “Not Live” to
          “Live”.  The followers who have already been approved to join your closed group
          will see this post once it’s LIVE.
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          We do not police your posts.  However, as states in the Terms & Conditions, if we
          have report of inappropriate posts being made, we reserve the right to remove your
          posts and/or account.
        </Typography>
      </Grid>

      <Grid item className={classes.contentItem}>
        <Typography variant="h6" color="primary" className={classes.contentHeading}>
          3 - Public Group Post
        </Typography>
        <Typography variant="body1">
          Public posts can be viewed by anyone who follows you.  You don’t need to approve
          followers for public groups, however, you will have the option to remove those
          public followers on your Groups page.
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          Begin by assigning the post to a public group name you’ve created.  Your group
          name should reflect a group, event, study, series, topic, campaign, etc. and help
          identify your post to followers.  You will most likely have multiple public groups,
          so the group name should be unique and descriptive.  Examples might be “2020
          Prophetic Conference”, “Church ABC Sermon Notes”, “5-Day Bible Challenge”, and so
          on. Group names may be ones you use in posts once a week, once a year, once a season
          or for a short or one time usage.  And along with group names in your posts, to help
          identify you, followers will see your leader, church or ministry name as well as a
          title and date posted.
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          To go LIVE, you will need to toggle “Not Live” to “Live”.  Your public group followers
          will see this post once it’s LIVE.
        </Typography>
        <Typography variant="body1" className={classes.contentSpacer}>
          We do not police your posts.  However, as states in the Terms & Conditions, if we have
          report of inappropriate posts being made, we reserve the right to remove your posts
          and/or account.
        </Typography>
      </Grid>

      <Grid item className={classes.contentItem}>
        <Typography variant="h6" color="primary" className={classes.contentHeading}>
          Reliht Web App and Mobile App are Kingdom Tools
        </Typography>
        <Typography variant="body1">
          Reliht is designed to be a Kingdom tool, where leaders can post the good news of the
          Gospel of Jesus Christ, grow as leaders, and be a light to the world.&nbsp;
          <b>JESUS IS THE LIGHT OF THE WORLD.</b> Please honor Him in
          your posts.
        </Typography>
      </Grid>

      <Grid item className={classes.contentItem}>
        <Typography variant="h6" color="primary" className={classes.contentHeading}>
          Share With Others
        </Typography>
        <Typography variant="body1">
          Please invite your friends and family, church, co-workers — anyone who loves God or
          wants to learn more about Him — to use Reliht.  Create posts — notes and highlights
          — store, read again,
          share and encourage others. <b> We hope Reliht blesses you and many others.</b>
        </Typography>
      </Grid>

      <Grid item className={classes.quoteBlock}>
        Record the vision and inscribe it on tablets, that the one who reads it may run.
        Habakkuk 2:2
      </Grid>
    </Grid>
  );
}
